import { useState } from "react";
import { Check, Puzzle } from "lucide-react";
import { usd } from "@/lib/usd";

export const FEATURE_LIST = [
  ["timelines", "Timelines & Calendars", "Time-travel scrubber, branching history, custom calendars & ages"],
  ["maps", "Maps", "Map generator, pins, regions, travel paths & playback"],
  ["relationships", "Relationships & Family Trees", "Relationship web and generational family trees"],
  ["languages", "Languages", "Dictionaries, glyphs, pronunciation & audio"],
];
export const FEATURE_LABEL = Object.fromEntries(FEATURE_LIST.map(([k, l]) => [k, l]));

const per = (i) => (i === "year" ? "yearly" : "monthly");
export const featPrice = (prices, f, interval) => prices?.[`feat_${f}_${per(interval)}`];

export const BuildPlan = ({ prices, interval, initial = [], initialCommercial = false, action }) => {
  const [sel, setSel] = useState(new Set(initial));
  const [commercial, setCommercial] = useState(initialCommercial);
  const toggle = (k) => setSel((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const total = [...sel].reduce((t, f) => t + (featPrice(prices, f, interval) || 0), 0) + (commercial && sel.size ? featPrice(prices, "commercial", interval) || 0 : 0);
  const full = prices?.[commercial ? `commercial_${per(interval)}` : per(interval)];
  const all = sel.size === FEATURE_LIST.length;
  return (
    <div className="mt-8 rounded-md border border-[#2D3442] bg-[#0F131A] p-7" data-testid="build-plan">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FBBF24]"><Puzzle className="h-3.5 w-3.5" />Build your own</div>
      <p className="mt-2 text-sm text-[#A8A090]">Only need some Pro tools? Pick just the ones you use and pay less.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {FEATURE_LIST.map(([k, label, desc]) => {
          const on = sel.has(k);
          return (
            <button key={k} type="button" onClick={() => toggle(k)} data-testid={`build-feature-${k}`} aria-pressed={on}
              className={`flex items-start gap-3 rounded-md border p-4 text-left transition-colors ${on ? "border-[#F59E0B]/70 bg-[#F59E0B]/5" : "border-[#2D3442] hover:border-[#F59E0B]/40"}`}>
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-[#F59E0B] bg-[#F59E0B] text-[#0B0D10]" : "border-[#2D3442]"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
              <span className="flex-1"><span className="flex items-baseline justify-between gap-2 text-[#F3EBDD]">{label}<span className="text-sm text-[#FBBF24]">{featPrice(prices, k, interval) == null ? "…" : usd(featPrice(prices, k, interval))}</span></span>
                <span className="mt-0.5 block text-xs text-[#A8A090]">{desc}</span></span>
            </button>
          );
        })}
      </div>
      <label className="mt-4 flex items-center gap-2.5 text-sm text-[#D9D0C1]" data-testid="build-commercial-row">
        <input type="checkbox" checked={commercial} onChange={(e) => setCommercial(e.target.checked)} className="h-4 w-4 accent-[#F59E0B]" data-testid="build-commercial-toggle" />
        Add a commercial-use license (+${featPrice(prices, "commercial", interval) ?? "…"}) for work you sell
      </label>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-[#2D3442] pt-5">
        <div>
          <span className="font-display text-4xl text-[#F3EBDD]" data-testid="build-total">${total.toFixed(2).replace(/\.00$/, "")}</span>
          <span className="ml-2 text-sm text-[#A8A090]">/ {interval === "year" ? "year" : "month"}</span>
          {sel.size > 0 && full && total >= full && <div className="mt-1 text-xs text-[#34D399]" data-testid="build-hint">Full {commercial ? "Commercial" : "Pro"} is ${full}, so it's the better deal</div>}
        </div>
        {action({ features: [...sel], commercial, disabled: !sel.size || all, all })}
      </div>
    </div>
  );
};
